import { useAuthStore } from '@/stores/auth';
import { useClientPortalStore } from '@/stores/clientPortal';
import { useTenantStore } from '@/stores/tenant';
import { useUserStore } from '@/stores/userStore';
import { resolvePreferredTeamTenantId } from '~/lib/teamDestination';
import { buildTenantWorkspaceUrl, resolveWorkspaceOrigin } from '~/lib/tenantLinks';

export function isExternalNavigationTarget(target?: string | null): boolean {
    const value = String(target || '').trim();
    return /^https?:\/\//i.test(value) || value.startsWith('//');
}

export function normalizeAuthRedirectTarget(target?: string | null): string {
    const value = String(target || '').trim();
    if (!value) {
        return '';
    }

    if (!isExternalNavigationTarget(value)) {
        return value;
    }

    try {
        const currentOrigin = import.meta.client ? window.location.origin : '';
        const parsed = currentOrigin ? new URL(value, currentOrigin) : new URL(value);
        if (!currentOrigin || parsed.origin === currentOrigin) {
            return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
        }

        return parsed.toString();
    } catch {
        return value;
    }
}

export async function resolvePostAuthDestination(options?: {
    explicitRedirect?: string | null,
    authSource?: 'login' | 'client-login' | null,
    tenantId?: string | null,
}): Promise<string> {
    const explicitRedirect = normalizeAuthRedirectTarget(options?.explicitRedirect);
    if (explicitRedirect) {
        return explicitRedirect;
    }

    const authSource = options?.authSource === 'client-login' ? 'client-login' : 'login';
    const tenantId = String(options?.tenantId || '').trim() || null;

    const authStore = useAuthStore();
    const tenantStore = useTenantStore();
    const portalStore = useClientPortalStore();
    const userStore = useUserStore();
    const runtimeConfig = useRuntimeConfig();
    const { getHostname, isCustomDomain } = useDomainResolver();
    const auth = useNuxtApp().$auth;
    const user = authStore.user || auth.currentUser;
    const accessUnavailableError = () => createError({
        statusCode: 503,
        statusMessage: 'Workspace access could not be loaded. Please refresh and try again.',
    });

    if (!user) {
        return authSource === 'client-login' ? '/auth/client-login' : '/auth/login';
    }

    if (!userStore.user || userStore.user.id !== user.uid) {
        const profileResolved = await userStore.fetchUser(user.uid);
        if (!profileResolved) {
            throw accessUnavailableError();
        }
    }

    if (authSource === 'client-login' && userStore.hasClientAccess) {
        const portalClientSlug = await portalStore.findFirstClientSlug({ tenantHint: tenantId });
        if (portalClientSlug) {
            return `/portal/${portalClientSlug}`;
        }

        if (!userStore.hasTeamAccess) {
            return '/portal';
        }
    }

    const shouldHydrateTenants = tenantStore.loadedForUserId !== user.uid
        || (!tenantStore.currentTenantId && tenantStore.tenants.length === 0);

    if (shouldHydrateTenants) {
        const membershipsResolved = await tenantStore.fetchUserTenants(user.uid);
        if (!membershipsResolved) {
            throw accessUnavailableError();
        }
    }

    if (tenantStore.tenants.length > 0) {
        const firstTenantId = resolvePreferredTeamTenantId({
            memberships: tenantStore.tenants,
            currentTenantId: tenantStore.currentTenantId,
            tenantHint: tenantId,
        });
        if (firstTenantId) {
            const currentHostname = getHostname();
            if (import.meta.client && isCustomDomain(currentHostname)) {
                const workspaceOrigin = resolveWorkspaceOrigin({
                    appDomain: String(runtimeConfig.public.appDomain || ''),
                    fallbackOrigin: window.location.origin,
                });
                return buildTenantWorkspaceUrl({
                    origin: workspaceOrigin,
                    tenantSlug: firstTenantId,
                    path: '/my-hub',
                });
            }

            return `/${firstTenantId}/my-hub`;
        }
    }

    if (userStore.hasClientAccess) {
        const portalClientSlug = await portalStore.findFirstClientSlug({ tenantHint: tenantId });
        if (portalClientSlug) {
            return `/portal/${portalClientSlug}`;
        }

        return '/portal';
    }

    return '/onboarding';
}
